import { Table, Button, Input, Label, Alert, Modal, ModalBody, ModalFooter, ModalHeader, FormGroup, Col, Row } from "reactstrap";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { DASHBOARD_ROOT } from "../../components/Routes/routingConstants";
import { useHistory } from "react-router-dom";
import { useUserQuestionnaires } from "../../hooks/useUserQuestionnaires";
import { addUserQuestionnaires, editUserQuestionnaires, removeUserQuestionnaires } from "../../graphql/content-management/addUserQuestionnaires";
import { UserQuestionnaires } from "../../interfaces/models/UserQuestionnaire";
import { useEffect, useRef, useState } from "react";
import { FilterTimerSearch } from "../../helper/searchHelper";
import Loading from "../../components/Loading";
import FormError from "../../components/Shared/Forms/FormError";
import { useMutation } from '@apollo/client';
import toast from "react-hot-toast";
import PromptDialog from "../../components/PromptDialog";
import { DateFormat, UserQuestionnaireType, UserQuestionnaireTypeObj } from "../../helper/constants";
import { Field, Form, Formik, FormikProps } from "formik";
import TextFormInput from "../../components/Shared/Forms/TextFormInput";
import DateTime from "react-datetime";
import { HandleReformatDate } from '../../helper/Utilities';
import AddQuestionnaireValidator from "../../validators/AddQuestionnaireValidator";
import moment from "moment";

//#region Interfaces
interface ModalProperties {
    name : string;
    buttonName : string;
}

interface FilterParams {
    search : string;
    status : number;
}

interface QuestionnaireInputs {
    name? : string;
    type? : number;
    preset? : number;
    startDate? : string;
    endDate? : string;
}
//#endregion Interfaces

const UserQuestionnairesPage = () => {

//#region Initializations
    const breadcrumbsData = [
        { name: 'Content List', path: `${DASHBOARD_ROOT}/content-management/` },
        { name: 'Edit Questionnaires', path: `${DASHBOARD_ROOT}/content-management/questionnaires` },
    ];

    const startDateQuarters : string[] = ["01-01","04-01","07-01","10-01"];
    const endDateQuarters : string[] = ["03-31","06-30","09-30","12-31"];
    const quarters : string[] = ["Custom","First Quarter","Second Quarter","Third Quarter","Fourth Quarter"];
    const yearNow : number = new Date().getFullYear();

    const { data, loading, error } = useUserQuestionnaires();
    const [addUserQuestionnairesHook] = useMutation(addUserQuestionnaires,{
        onCompleted : () =>{
            toast.success("Questionnaire was successfully added. Please wait page is Reloading");
        },
        onError : (err) =>{
            toast.error(err.graphQLErrors[0].message);
        }
    });
    const [editUserQuestionnairesHook] = useMutation(editUserQuestionnaires,{
        onCompleted : () =>{
            toast.success("Questionnaire was successfully updated. Please wait page is Reloading");
        },
        onError : (err) =>{
            toast.error(err.graphQLErrors[0].message);
        }
    });

    const [removeUserQuestionnairesHook] = useMutation(removeUserQuestionnaires,{
        onCompleted : () =>{
            toast.success("Questionnaire was successfully removed. Please wait page is Reloading");
        },
        onError : (err) =>{
            // toast.error("Something went wrong whilst deleting the Questionnaire. Please try again or if issue persists, please contact us");
            toast.error(err.graphQLErrors[0].message);
        }
    });

    const [questions, setQuestions] = useState<UserQuestionnaires[]>([]);
    const [questionsFiltered, setQuestionsFiltered] = useState<UserQuestionnaires[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [filters, setFilters] = useState<FilterParams>({ search : "", status : 0});
    const [startDate, setStartDate] = useState<Date>(null);
    const [endDate, setEndDate] = useState<Date>(null);
    const [questionInputs, setQuestionInputs] = useState<QuestionnaireInputs>({});
    const formikRef = useRef<FormikProps<QuestionnaireInputs>>(null);
    const [selectedQuestion, setSelectedQuestion] = useState<UserQuestionnaires>(null);
    const [modal, setModal] = useState<ModalProperties>({
        name : "",
        buttonName : ""
    });
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);

    const [isOpenDelete, setIsOpenDelete] = useState<boolean>(false);

    const history = useHistory();

    const HandleView = (question : UserQuestionnaires) => {
        history.push(`${DASHBOARD_ROOT}/content-management/questionnaires/${question._id}`);
    }

    const Reloader = () => {
        setTimeout(() => {
            window.location.reload();
        },1000)
    }

    const HandleSearch = (search : string, status : number) => {
        search = search?.trim().toLowerCase();
        const dateNow = new Date();

        FilterTimerSearch(setIsSearching, () => {
            let filtered = questions.filter((question) => {
                return question.name.toLowerCase().includes(search.toLowerCase()) &&
                (status === 0 || 
                (
                    (status === 1 && question.end_date && new Date(question.end_date) > dateNow && question.type !== UserQuestionnaireType.NOT_IN_USE) ||
                    (status === 1 && question.type === UserQuestionnaireType.ONBOARDING)
                ) || 
                (
                    (status === 2 && question.end_date && new Date(question.end_date) < dateNow)  || 
                    (status === 2  && question.type === UserQuestionnaireType.NOT_IN_USE)
                )
            )
                ;
            });
            setQuestionsFiltered(filtered);
        });
    }

    useEffect(() => {
        if(data) {
            setQuestions(data.user_questionnaires);
            setQuestionsFiltered(data.user_questionnaires);
        }
    },[data]);
//#endregion Initializations
//#region Add and Update Functions

    const ToggleAddModal = () => {
        setIsAddModalOpen(!isAddModalOpen);

        setQuestionInputs({
            name : "",
            type : 1,
            startDate : "",
            endDate : "",
        })
    }

    const HandleOpenAddModal = () => {
        setQuestionInputs({
            name : "",
            type : 1,
            preset : 0,
            startDate : "",
            endDate : "",
        });
        setStartDate(null);
        setEndDate(null);
        setModal({
            name : "Add Questionnaire",
            buttonName : "Add"
        });
        setIsAddModalOpen(true);
    }

    const HandleOpenUpdateModal = (item : UserQuestionnaires) => {
        setSelectedQuestion(item);
        setQuestionInputs({
            name : item.name,
            type : item.type,
            endDate : item.end_date ? moment(item.end_date).format("YYYY-MM-DD") : "",
            startDate : item.start_date ? moment(item.start_date).format("YYYY-MM-DD") : ""
        });
        setModal({
            name : "Edit Questionnaire",
            buttonName : "Edit"
        });
        setIsAddModalOpen(true);
        
    }

    const HandleSubmit = () => {
        formikRef.current.submitForm();
    }

    const HandleOnTypeAndYearChanged = (type : number, preset : number) => {
        const  { setFieldValue } = formikRef.current
        setFieldValue("type",Number(type));
        setFieldValue("preset",Number(preset || 0));

        if(type !== UserQuestionnaireType.SCHEDULED) {
            setFieldValue("startDate","");
            setFieldValue("endDate","");
            setStartDate(null);
            setEndDate(null);
            return;
        }
        if(preset === 0 || typeof preset === "undefined") {
            return;
        }

        const dateStart = yearNow + "-" + startDateQuarters[preset - 1];
        const dateEnd = yearNow + "-" + endDateQuarters[preset - 1];
        setFieldValue("startDate",dateStart);
        setFieldValue("endDate",dateEnd);
        setStartDate(new Date(dateStart));
        setEndDate(new Date(dateEnd));
    }

    const HandleAddSubmit = async (formData : QuestionnaireInputs) => {
        setIsProcessing(true);
        let isProcessed = false;
        
        if(modal.buttonName === "Add") {
            isProcessed = await addUserQuestionnairesHook({
                variables : {
                    record : {
                        name : formData.name,
                        type : Number(formData.type),
                        start_date : new Date(formData.startDate),
                        end_date : new Date(formData.endDate),
                        year : new Date(formData.startDate).getFullYear()
                    }
                }
            }).then((res) => res.errors ? false : true).catch(()=>false);
            
        }
        else { 
            isProcessed = await editUserQuestionnairesHook({
                variables : {
                    _id : selectedQuestion._id,
                    record : {
                        name : formData.name,
                        type : Number(formData.type),
                        start_date : new Date(formData.startDate),
                        end_date : new Date(formData.endDate),
                        year : new Date(formData.startDate).getFullYear()
                    }
                }
            }).then((res) => res.errors ? false : true).catch(()=>false);
        }


        if(!isProcessed) {
            setIsProcessing(false);
            return;
        }

        Reloader();
    }
//#endregion Add and Update Functions
//#region Delete Functions

    const HandleOpenDelete = (item : UserQuestionnaires) => {
        setSelectedQuestion(item);
        setIsOpenDelete(true);
    }

    const HandleDeleteSubmit =  async () => {
        setIsProcessing(true);

        const isDeleted = await removeUserQuestionnairesHook({
            variables : {
                _id : selectedQuestion._id
            }
        }).then((res) => res.errors ? false : true).catch(()=>false);

        if(!isDeleted) {
            setIsProcessing(false);
            return;
        }

        Reloader();
    }
//#endregion Delete Functions

    return (
        <>
            <Breadcrumbs 
                crumbs={breadcrumbsData}
            />
            {
                error && <Alert color="danger">An error occurred while fetching questionnaires</Alert>
            }
            <div className="d-flex justify-content-end mb-2">
                <Button color="primary" onClick={HandleOpenAddModal}>Add</Button>
            </div>
            <Row>
                <Col>
                    <div className="d-flex align-items-center mb-3">
                        <Label className="mr-2">Search: </Label>
                        <Input type="text" placeholder="Search Questionnaire Name Here" 
                        value={filters.search} 
                        onChange={(e)=> { setFilters((prev) => ({...prev, search : e.target.value}))}} 
                        onKeyUp={(e) => { HandleSearch(e.currentTarget.value, filters.status)}} />
                    </div>
                </Col>
                <Col>
                    <div className="d-flex align-items-center mb-3">
                        <Label className="mr-2">Search: </Label>
                        <Input type="select" placeholder="Search Questionnaire Name Here" 
                        value={filters.status} 
                        onChange={(e)=> { HandleSearch(filters.search, Number(e.currentTarget.value)); setFilters((prev) => ({...prev, status : Number(e.target.value)}))}} 
                        >
                            <option value={0}>All</option>
                            <option value={1}>Active</option>
                            <option value={2}>Inactive</option>
                        </Input>
                    </div>
                </Col>
                
            </Row>
            <div className="table-responsive  table-max-height">
                <Table bordered className=" bg-white">
                    <thead className="sticky-top bg-white">
                        <tr>
                            <th>Questionnaire Name</th>
                            <th>Type</th>
                            <th>Start to End Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {
                            !loading && !isSearching && questionsFiltered.length === 0 &&
                            <tr className="text-center">
                                <td colSpan={4}>No Questionnaires Found</td>
                            </tr>
                        }
                        {
                            loading || isSearching 
                            ? 
                                <tr>
                                    <td colSpan={4} className="text-center"><Loading /></td>
                                </tr> 
                            :
                                questionsFiltered.map((question) => {
                                    return (
                                        <tr key={question._id}>
                                            <td>{question.name}</td>
                                            <td>{UserQuestionnaireTypeObj[question.type - 1].label} {question.year ? `(${question.year})` : ""}</td>
                                            <td>{question.start_date ? moment(question.start_date).format(DateFormat) + " to " + moment(question.end_date).format(DateFormat) : "-"}</td>
                                            <td>
                                                <Button color="primary" size="sm" onClick={() => { HandleOpenUpdateModal(question) }}>Edit</Button>
                                                <Button color="primary" size="sm" onClick={() => { HandleView(question) }}>View</Button>
                                                <Button color="danger" size="sm" onClick={() => { HandleOpenDelete(question) }}>Delete</Button>
                                            </td>
                                        </tr>
                                    )
                                })
                        }
                    </tbody>
                </Table>
            </div>

            <Modal
                id="questionnaire-add-modal"
                backdrop={true}
                isOpen={isAddModalOpen}>
                <ModalHeader toggle={ToggleAddModal} className="pb-0">
                    <p className="text-lg">{modal.name}</p>
                </ModalHeader>
                <ModalBody>
                    <Formik
                        innerRef={formikRef}
                        initialValues={questionInputs}
                        validationSchema={AddQuestionnaireValidator}
                        onSubmit={async (value : QuestionnaireInputs) =>{
                            await HandleAddSubmit(value);
                        }}
                    >
                        {({touched, errors, setFieldValue, values}) => (
                            <Form>
                                <TextFormInput
                                    name={"name"}
                                    placeholder="Ex. Onboarding Questions 2024"
                                    label="Questionnaire Name"
                                    required={true}
                                />
                                <FormGroup>
                                    <Label> Questionnaire Type *</Label>
                                    <Field as="select" name="type" className="form-control" required onChange={(e) => {
                                        HandleOnTypeAndYearChanged(Number(e.target.value), values.preset);
                                    }}>
                                        {
                                            UserQuestionnaireTypeObj.map((item, key) => 
                                                <option key={key} value={item.value}>{item.label}</option>
                                            )
                                        }
                                    </Field>
                                </FormGroup>
                                
                                {
                                    values.type > UserQuestionnaireType.ONBOARDING && 
                                    <>
                                        <FormGroup>
                                            <Label> Preset Dates </Label>
                                            <Field as="select" name="preset" className="form-control" required onChange={(e) => {
                                                HandleOnTypeAndYearChanged(values.type ,Number(e.target.value));
                                            }}>
                                                {
                                                    quarters.map((item, key) => 
                                                        <option key={key + 1} value={key}>{item}</option>
                                                    )
                                                }
                                            </Field>
                                        </FormGroup>
                                        <FormGroup className="mb-0">
                                            <Label>Start Date *</Label>
                                            <DateTime 
                                                className="w-100" 
                                                onChange={(date) => {
                                                    const reformated = HandleReformatDate(date);
                                                    setFieldValue("startDate",reformated);
                                                    setStartDate(new Date(reformated));   
                                                }} 
                                                
                                                dateFormat={DateFormat} 
                                                timeFormat={false} 
                                                inputProps={{ readOnly: true, className:"form-control bg-white"}} 
                                                initialValue={new Date(values.startDate)}
                                                value={startDate}
                                            />
                                            <FormError>{touched.startDate && (errors.startDate)}</FormError>
                                        </FormGroup>
                                        <FormGroup className="mb-0">
                                            <Label>End Date *</Label>
                                            <DateTime 
                                                className="w-100" 
                                                onChange={(date) => {
                                                    const reformated = HandleReformatDate(date);
                                                    setFieldValue("endDate",reformated) 
                                                    setEndDate(new Date(reformated));   
                                                }}
                                                dateFormat={DateFormat} 
                                                timeFormat={false}
                                                inputProps={{  readOnly: true,className:"form-control bg-white"}} 
                                                initialValue={new Date(values.endDate)}
                                                value={endDate}
                                            />
                                            <FormError>{touched.endDate && (errors.endDate)}</FormError>
                                        </FormGroup>
                                    </>
                                }
                                
                            </Form>
                        )}
                        
                    </Formik>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" disabled={isProcessing} onClick={ToggleAddModal} >Close </Button>
                    <Button color="primary" disabled={isProcessing} onClick={HandleSubmit} >{modal.buttonName}</Button>
                </ModalFooter>
            </Modal>

            <PromptDialog
                isOpen={isOpenDelete}
                onCancel={() => { setIsOpenDelete(false); }}
                onConfirm={HandleDeleteSubmit}
                title="Delete Questionnaire"
                promptMessage="Are you sure you want to delete this Questionnaire?"
                confirmBtnTxt="Delete"
                isOnProcess={isProcessing}
            />
                        
        </>
    )
}

export { UserQuestionnairesPage as UserQuestionnaires };